/**
 * Visa Success Probability Checker — public landing page (no login required).
 * Collects a minimum lead + key risk inputs, calls /api/visa-probability/public,
 * and renders the ranked country cards + AI advisory.
 */
import { useState, useEffect } from 'react';
import { Sparkles, ShieldCheck, AlertTriangle, ChevronRight, Loader2, Globe2, Trophy, Flag, Phone, MessageCircle, Mail, ArrowRight, CheckCircle2, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';

const SEVERITY = {
  critical: 'bg-rose-100 text-rose-800 border-rose-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  moderate: 'bg-amber-100 text-amber-800 border-amber-300',
  info: 'bg-sky-100 text-sky-800 border-sky-300',
};

const COLOR_BG = {
  emerald: 'bg-emerald-50 border-emerald-300 text-emerald-800',
  amber: 'bg-amber-50 border-amber-300 text-amber-800',
  orange: 'bg-orange-50 border-orange-300 text-orange-800',
  rose: 'bg-rose-50 border-rose-300 text-rose-800',
};

const COLOR_RING = {
  emerald: 'text-emerald-500',
  amber: 'text-amber-500',
  orange: 'text-orange-500',
  rose: 'text-rose-500',
};

function ProbabilityRing({ value, color = 'emerald' }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative w-16 h-16">
      <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
        <circle cx="32" cy="32" r={r} className="text-gray-200 dark:text-slate-700" stroke="currentColor" strokeWidth="6" fill="none" />
        <circle cx="32" cy="32" r={r} className={COLOR_RING[color]} stroke="currentColor" strokeWidth="6" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-800 dark:text-white">
        {Math.round(value)}%
      </div>
    </div>
  );
}

export default function VisaProbabilityPage() {
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    nationality: '',
    bachelors_percentage: '',
    english_overall_band: '',
    available_funds_gbp: '',
    annual_tuition_gbp: 20000,
    study_gap_years: '',
    sponsor_relation: '',
    previous_visa_refusals: '',
    target_country: '',
  });

  // Rotating loading-step messages while the GPT call is in-flight (8–15s)
  const LOADING_STEPS = [
    'Validating your profile inputs…',
    'Matching against UK, US, Canada, Australia & EU visa policies…',
    'Scoring funds, academics and language vs each country…',
    'Running AI advisory (GPT-5.2)…',
    'Calibrating against recent visa outcomes…',
    'Ranking your best matches…',
  ];

  useEffect(() => {
    if (!loading) {
      setLoadingStep(0);
      return undefined;
    }
    const t = setInterval(() => {
      setLoadingStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, 2200);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.nationality) {
      toast.error('Email and nationality are required');
      return;
    }
    setLoading(true);
    try {
      const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
      const payload = {
        ...form,
        bachelors_percentage: numOrNull(form.bachelors_percentage),
        english_overall_band: numOrNull(form.english_overall_band),
        available_funds_gbp: numOrNull(form.available_funds_gbp),
        annual_tuition_gbp: numOrNull(form.annual_tuition_gbp),
        study_gap_years: numOrNull(form.study_gap_years),
        include_advisory: true,
      };
      const { data } = await api.post('/visa-probability/public', payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not run prediction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-900 dark:to-slate-800">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Visa Success Probability Checker</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl">
          AI-powered, country-by-country predictions for your student visa application — ranked, scored, and explained in 10 seconds.
        </p>

        {result ? (
          <ResultView result={result} form={form} onReset={() => setResult(null)} />
        ) : (
          <form
            onSubmit={submit}
            className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-6 md:p-8 shadow-sm"
            data-testid="visa-probability-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input value={form.full_name} onChange={update('full_name')} placeholder="Your name" data-testid="visa-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" data-testid="visa-email" required />
              </div>
              <div>
                <Label>Phone (WhatsApp preferred) <span className="text-xs text-gray-400">— so our team can reach you</span></Label>
                <Input type="tel" value={form.phone} onChange={update('phone')} placeholder="+92 300 1234567" data-testid="visa-phone" />
              </div>
              <div>
                <Label>Nationality (passport country) *</Label>
                <Input value={form.nationality} onChange={update('nationality')} placeholder="e.g. Pakistan, India, Nigeria" data-testid="visa-nationality" required />
              </div>
              <div>
                <Label>Bachelor&apos;s percentage</Label>
                <Input type="number" min="0" max="100" step="0.1" value={form.bachelors_percentage} onChange={update('bachelors_percentage')} placeholder="e.g. 65" data-testid="visa-bachelors" />
              </div>
              <div>
                <Label>IELTS-equivalent overall band</Label>
                <Input type="number" step="0.5" min="0" max="9" value={form.english_overall_band} onChange={update('english_overall_band')} placeholder="e.g. 6.5" data-testid="visa-english" />
              </div>
              <div>
                <Label>Available funds (GBP)</Label>
                <Input type="number" min="0" value={form.available_funds_gbp} onChange={update('available_funds_gbp')} placeholder="e.g. 25000" data-testid="visa-funds" />
              </div>
              <div>
                <Label>Years since last study</Label>
                <Input type="number" min="0" max="20" value={form.study_gap_years} onChange={update('study_gap_years')} placeholder="e.g. 2" data-testid="visa-gap" />
              </div>
              <div>
                <Label>Primary sponsor relationship</Label>
                <Select value={form.sponsor_relation} onValueChange={update('sponsor_relation')}>
                  <SelectTrigger data-testid="visa-sponsor"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {['Self','Father','Mother','Brother','Sister','Uncle','Aunt','Friend','Other'].map(o => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Any previous visa refusal? <span className="text-xs text-gray-400">(optional — disclose honestly for accurate scoring)</span></Label>
                <Input value={form.previous_visa_refusals} onChange={update('previous_visa_refusals')} placeholder="e.g. UK 2023 - low funds" data-testid="visa-refusal" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full mt-6 bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 text-white font-semibold h-12" data-testid="visa-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {loading ? 'AI is analysing your profile…' : 'Predict My Visa Success'}
            </Button>
            {loading && (
              <div className="mt-5 rounded-xl border border-teal-200 bg-teal-50/60 dark:bg-slate-900/40 p-4" data-testid="visa-loading-steps">
                <p className="text-xs font-semibold uppercase tracking-wider text-teal-700 mb-3">
                  Running prediction · usually takes 10–15 seconds
                </p>
                <ul className="space-y-2">
                  {LOADING_STEPS.map((step, i) => {
                    const done = i < loadingStep;
                    const active = i === loadingStep;
                    return (
                      <li key={i} className={`flex items-center gap-2 text-sm transition-opacity ${i <= loadingStep ? 'opacity-100' : 'opacity-40'}`}>
                        {done ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        ) : active ? (
                          <Loader2 className="w-4 h-4 text-teal-600 animate-spin shrink-0" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />
                        )}
                        <span className={done ? 'text-gray-600 line-through decoration-emerald-300/60' : active ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500'}>
                          {step}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-3 text-center">
              <ShieldCheck className="inline w-3 h-3 mr-1" />
              Your data is private and used only for this assessment.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function ResultView({ result, form, onReset }) {
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);

  const handleEmailReport = async () => {
    if (!form?.email) {
      toast.error('Email is required to send the report');
      return;
    }
    setEmailing(true);
    try {
      const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
      const payload = {
        email: form.email,
        full_name: form.full_name || null,
        phone: form.phone || null,
        nationality: form.nationality || null,
        bachelors_percentage: numOrNull(form.bachelors_percentage),
        english_overall_band: numOrNull(form.english_overall_band),
        available_funds_gbp: numOrNull(form.available_funds_gbp),
        annual_tuition_gbp: numOrNull(form.annual_tuition_gbp),
        study_gap_years: numOrNull(form.study_gap_years),
        sponsor_relation: form.sponsor_relation || null,
        previous_visa_refusals: form.previous_visa_refusals || null,
        target_country: form.target_country || null,
      };
      const { data } = await api.post('/visa-probability/email-report', payload);
      if (data?.success) {
        setEmailed(true);
        toast.success(data.message || 'Report emailed — check your inbox.');
      } else {
        toast.error(data?.message || 'Could not send the report. Please try again.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || 'Failed to email report');
    } finally {
      setEmailing(false);
    }
  };

  const top = result.ranking?.[0];
  const topProb = top?.probability || 0;
  const ranking = result.ranking || [];
  const bestPicks = ranking.filter((c) => c.probability >= 70);
  const riskyPicks = ranking.filter((c) => c.probability < 60);

  // Adaptive headline CTA based on top probability band
  const ctaTone =
    topProb >= 75 ? 'strong' : topProb >= 60 ? 'moderate' : 'risky';

  const ctaCopy = {
    strong: {
      title: 'Strong profile — let’s get you admitted',
      body: 'Your top match is well within the safe zone. Lock in your spot before deadlines close. Our counsellors can shortlist universities and start your application the same day.',
      primary: 'Apply Now with H E Direct',
      secondary: 'Chat on WhatsApp',
    },
    moderate: {
      title: 'Promising profile — needs careful preparation',
      body: 'Your profile shows real potential but a few gaps could hurt at the visa interview. Let H E Direct review your file, strengthen the weak spots, and submit it with maximum chance of approval.',
      primary: 'Get Expert File Review',
      secondary: 'Chat on WhatsApp',
    },
    risky: {
      title: 'High-risk profile — do not apply alone',
      body: 'Multiple risk factors are flagged. Applying without expert help significantly increases refusal odds. Contact H E Direct — our team can work with you to prepare a stronger file and improve your visa success rate.',
      primary: 'Contact H E Direct',
      secondary: 'Speak to a Counsellor',
    },
  }[ctaTone];

  const ctaTheme = {
    strong: 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-900',
    moderate: 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-900',
    risky: 'border-rose-300 bg-gradient-to-br from-rose-50 to-orange-50 text-rose-900',
  }[ctaTone];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="visa-probability-result">
      {/* Top headline card */}
      <div className={`rounded-2xl border-2 p-6 ${COLOR_BG[top?.color || 'emerald']}`}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <Trophy className="w-8 h-8" />
            <div>
              <p className="text-xs uppercase tracking-wide opacity-70">Best match</p>
              <h2 className="text-2xl font-bold">{top?.country}</h2>
              <Badge className="mt-1 bg-white/70 text-gray-800">{top?.band}</Badge>
            </div>
          </div>
          <ProbabilityRing value={top?.probability || 0} color={top?.color} />
        </div>
        {result.advisory && (
          <p className="mt-4 text-sm leading-relaxed" data-testid="visa-advisory">
            <Sparkles className="inline w-4 h-4 mr-1 -mt-0.5" />
            {result.advisory}
          </p>
        )}
      </div>

      {/* Email me this report (PDF) */}
      <div className="bg-white dark:bg-slate-800 border-2 border-dashed border-teal-300 dark:border-teal-700 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="email-report-block">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shrink-0">
          <FileDown className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-bold text-gray-900 dark:text-white">Email me this report as a PDF</h4>
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
            Branded, shareable PDF — keep a copy, send it to your parents or counsellor in one click.
          </p>
        </div>
        <Button
          onClick={handleEmailReport}
          disabled={emailing || emailed}
          className={`shrink-0 h-10 px-5 font-semibold ${emailed ? 'bg-emerald-600 hover:bg-emerald-600 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}`}
          data-testid="email-report-btn"
        >
          {emailing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
          ) : emailed ? (
            <><CheckCircle2 className="w-4 h-4 mr-2" /> Sent to {form?.email}</>
          ) : (
            <><Mail className="w-4 h-4 mr-2" /> Email Me My Report</>
          )}
        </Button>
      </div>

      {/* Risk flags */}
      {result.flags?.length > 0 && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5">
          <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Risk Flags
          </h3>
          <div className="flex flex-wrap gap-2">
            {result.flags.map((f) => (
              <div key={f.id} className={`text-xs px-3 py-1.5 rounded-full border ${SEVERITY[f.severity] || SEVERITY.info}`} title={f.detail}>
                <Flag className="inline w-3 h-3 mr-1" /> {f.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Country ranking grid */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-teal-500" /> All Destinations — Ranked
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="visa-country-ranking">
          {ranking.map((c, i) => (
            <div key={c.country} className={`rounded-lg border p-3 flex items-start gap-3 ${COLOR_BG[c.color]}`}>
              <span className="text-xs font-semibold opacity-70 w-5 pt-3">#{i + 1}</span>
              <ProbabilityRing value={c.probability} color={c.color} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{c.country}</p>
                <p className="text-xs opacity-80">{c.band}</p>
                {/* Inline contextual note per card */}
                {c.probability >= 70 ? (
                  <p className="mt-2 text-[11px] leading-snug flex items-start gap-1 opacity-90">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
                    Strong fit — proceed with confidence.
                  </p>
                ) : c.probability < 60 ? (
                  <p className="mt-2 text-[11px] leading-snug flex items-start gap-1 opacity-90">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    Higher risk — H E Direct can help strengthen your file.
                  </p>
                ) : (
                  <p className="mt-2 text-[11px] leading-snug flex items-start gap-1 opacity-90">
                    <Flag className="w-3 h-3 mt-0.5 shrink-0" />
                    Borderline — apply with expert guidance.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Best-pick summary line */}
        {bestPicks.length > 0 && (
          <p className="mt-4 text-sm text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3" data-testid="visa-best-summary">
            <CheckCircle2 className="inline w-4 h-4 mr-1 -mt-0.5" />
            <strong>Best options for you:</strong> {bestPicks.map((c) => c.country).join(', ')}. These destinations align well with your profile — you can confidently move ahead with the application process.
          </p>
        )}

        {/* Risky-pick summary line */}
        {riskyPicks.length > 0 && (
          <p className="mt-3 text-sm text-rose-800 dark:text-rose-200 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-3" data-testid="visa-risk-summary">
            <AlertTriangle className="inline w-4 h-4 mr-1 -mt-0.5" />
            <strong>Higher-risk for you:</strong> {riskyPicks.map((c) => c.country).join(', ')}. We strongly recommend you <strong>contact H E Direct</strong> before applying to these — our team will work with you to prepare your file, address risk factors, and improve your chances of a successful visa application.
          </p>
        )}
      </div>

      {/* Next actions per top destination */}
      {result.next_actions?.length > 0 && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5">
          <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Recommended Next Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {result.next_actions.map((na) => (
              <div key={na.country} className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
                <p className="font-semibold text-sm text-teal-600 mb-2">{na.country}</p>
                <ul className="space-y-1.5 text-xs text-gray-700 dark:text-gray-300">
                  {na.actions?.map((a, i) => (
                    <li key={i} className="flex items-start gap-1.5"><ChevronRight className="w-3 h-3 mt-0.5 text-teal-500 shrink-0" />{a}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Adaptive CTA — always shown, tone changes with risk band */}
      <div className={`rounded-2xl border-2 p-6 ${ctaTheme}`} data-testid="visa-cta-block">
        <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
          {ctaTone === 'risky' ? <AlertTriangle className="w-5 h-5" /> : ctaTone === 'moderate' ? <Flag className="w-5 h-5" /> : <Trophy className="w-5 h-5" />}
          {ctaCopy.title}
        </h3>
        <p className="text-sm leading-relaxed mb-4">{ctaCopy.body}</p>
        <div className="flex flex-wrap gap-3">
          <a
            href="https://wa.me/923268096371"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
            data-testid="visa-cta-whatsapp"
          >
            <MessageCircle className="w-4 h-4" />
            {ctaCopy.secondary}
          </a>
          <a
            href="mailto:admin.team@hedirect.org?subject=Visa%20Probability%20Follow-up"
            className="inline-flex items-center gap-2 bg-white/80 hover:bg-white border border-current/30 px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
            data-testid="visa-cta-email"
          >
            <Mail className="w-4 h-4" />
            {ctaCopy.primary}
            <ArrowRight className="w-3 h-3" />
          </a>
        </div>
        <p className="mt-4 text-xs opacity-80 flex items-center gap-1.5">
          <Phone className="w-3 h-3" />
          Or call us directly: <strong>+92 326 8096371</strong> · Mon–Sat · 10:00 AM–7:00 PM PKT
        </p>
      </div>

      <div className="flex justify-between items-center pt-2">
        <p className="text-xs text-gray-500">Powered by GPT-5.2 · Calibrated on observed visa outcomes</p>
        <Button variant="outline" onClick={onReset} data-testid="visa-reset">Run another</Button>
      </div>
    </div>
  );
}
